import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, Sparkles, User, ChevronDown, ChevronUp, ArrowRight, Mic, MicOff, Clock, Heart } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import AIMarkdown from '@/components/ai/AIMarkdown';
import { cn } from '@/lib/utils';
import { useWebSpeechRecording } from '@/hooks/useWebSpeechRecording';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigatorChat } from '@/components/dashboard/client/ai-navigator/useNavigatorChat';
import { saveRedirectPath } from '@/utils/auth-redirect';
import { useFastNavigation } from '@/hooks/useFastNavigation';
import { trackAIEvent, trackAIEventBeacon, classifyInputLength, classifyProblemType, classifyAIResponse } from '@/utils/aiTrace';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { getGuestNavigatorSessionId } from '@/components/dashboard/client/ai-navigator/navigatorConversationStorage';

import SessionResumePrompt from '@/components/dashboard/client/ai-navigator/SessionResumePrompt';
import { detectCrisis, forceCrisisRedirect } from '@/utils/crisisDetection';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import FrameworkCards from '@/components/service-selection/FrameworkCards';
import { aiText, type AiTextMap } from '@/i18n/ai-conversation';


type HomepageIntent = 'client' | 'practitioner' | 'learn';
const INTENT_STORAGE_KEY = 'reso_homepage_ai_intent_v1';
const PRACTITIONER_GUEST_TURN_LIMIT = 3;
// Anonymous client visitors get 15 conversation rounds before we softly
// invite them to sign in. Learn intent has no limit. Logged-in clients are
// unlimited (subject to normal AI rate limits).
const CLIENT_GUEST_TURN_LIMIT = 15;

interface WellnessConciergeProps {
  onStageRecommended?: (stage: number) => void;
}

const CRISIS_HELP_URL = 'https://resonow.org/crisis-resources';

const STAGE_NAMES: Record<number, { en: string; zh: string }> = {
  0: { en: 'Crisis Intervention', zh: '危机干预' },
  1: { en: 'Harmonize Mind & Body', zh: '身心调和' },
  2: { en: 'Analysis & Investigation', zh: '分析与探究' },
  3: { en: 'Transpersonal Dive', zh: '超个人深潜' },
  4: { en: 'Actualization & Integration', zh: '实现与整合' },
};

// ── Concern category chips ──
const CONCERN_CHIPS_EN = [
  { label: 'Anxiety', value: 'anxiety' },
  { label: 'Relationship', value: 'relationship difficulties' },
  { label: 'Sleep', value: 'sleep problems' },
  { label: 'Burnout', value: 'burnout and exhaustion' },
  { label: 'Feeling lost', value: 'feeling lost' },
];

const CONCERN_CHIPS_ZH = [
  { label: '焦虑', value: '焦虑' },
  { label: '关系', value: '关系困扰' },
  { label: '睡眠', value: '睡眠问题' },
  { label: '倦怠', value: '倦怠和疲惫' },
  { label: '迷茫', value: '感到迷茫' },
];

// ── Duration chips ──
const DURATION_CHIPS_EN = [
  { label: 'Just now', value: 'just started recently' },
  { label: 'Weeks', value: 'for several weeks' },
  { label: 'Months', value: 'for months' },
  { label: 'Long-term', value: 'for a long time, possibly years' },
];

const DURATION_CHIPS_ZH = [
  { label: '刚开始', value: '最近刚开始' },
  { label: '几周了', value: '持续了几周' },
  { label: '几个月', value: '已经几个月了' },
  { label: '长期', value: '长期存在，可能好几年了' },
];

// ── Dynamic follow-up chip pools ──
// Chips must align with the conversation stage and the AI's most recent
// response_type. They should *continue* the dialogue, not jump ahead to
// recommendations before the user has shared enough context.
type ChipStage = 'early' | 'mid' | 'late' | 'conversion';

const DYNAMIC_CHIPS: Record<ChipStage, { en: string[]; zh: string[] }> = {
  // Input is too short / vague — invite the user to express more.
  early: {
    en: [
      'Tell me a bit more',
      'What’s been hardest?',
      'When did this start?',
      'How does it feel in your body?',
    ],
    zh: [
      '可以再多说一点吗？',
      '最让你难受的是什么？',
      '这种感觉是什么时候开始的？',
      '身体上有什么反应？',
    ],
  },
  // User has shared something — help them add structure / specifics.
  // Includes "Go deeper" once enough context exists for the consciousness engine.
  mid: {
    en: [
      'Add more context',
      'How often does it happen?',
      'What have you already tried?',
      'Go deeper with analysis',
    ],
    zh: [
      '补充更多背景',
      '它出现得有多频繁？',
      '你之前尝试过什么方法？',
      '深入分析',
    ],
  },
  // Enough context — invite analysis / pathway exploration.
  late: {
    en: [
      'Go deeper with analysis',
      'What might help?',
      'Show me options',
      'Help me understand why',
    ],
    zh: [
      '深入分析',
      '有哪些可能有帮助的方式？',
      '给我看看选项',
      '帮我理解原因',
    ],
  },
  // AI has produced a recommendation — drive conversion, but still offer
  // the deep-analysis path for users who want the full picture first.
  conversion: {
    en: [
      'View practitioners',
      'Book a session',
      'Go deeper with analysis',
      'Save for later',
    ],
    zh: [
      '查看疗愈师',
      '预约一次咨询',
      '深入分析',
      '稍后再看',
    ],
  },
};

const SERVICE_INTENT_PATTERN = /\b(who can help|find (a )?(therapist|practitioner|healer)|book|booking|appointment|recommend(ed)? services?|see services?|service recommendation)\b|找(疗愈师|咨询师|服务)|预约|预订|推荐(服务|疗愈师|咨询师)|谁能帮/i;

/**
 * Parse [quick_reply: opt1 | opt2 | opt3] from AI response.
 * Returns { cleanContent, quickReplies } where cleanContent has the tag stripped.
 */
function parseQuickReplies(content: string): { cleanContent: string; quickReplies: string[]; hasFrameworkCards: boolean } {
  if (!content) return { cleanContent: '', quickReplies: [], hasFrameworkCards: false };
  try {
    // Extract quick_reply options first
    const findRe = /\[\s*quick_reply\s*:\s*([\s\S]+?)\]/i;
    const match = content.match(findRe);
    const options = match
      ? match[1].split('|').map((o) => o.replace(/\s+/g, ' ').trim()).filter(Boolean)
      : [];

    const hasFrameworkCards = /\[\s*framework_cards\s*\]/i.test(content);

    let out = content;
    // Strip framework_cards marker
    out = out.replace(/\[\s*framework_cards\s*\]/gi, '');
    // JSON metadata trailers
    out = out.replace(/\n?\{"phase":"[^"]+"\}\s*$/g, '');
    out = out.replace(/\n?\{"analysis_complete":[\s\S]*?\}\s*$/g, '');
    out = out.replace(/\n?\{"root_cause_complete":[\s\S]*?\}\s*$/g, '');
    out = out.replace(/\n?\{"therapy_recommendations":[\s\S]*?\}\s*$/g, '');
    // Bracketed control markers
    out = out.replace(/\[\s*(quick_reply|stage_name|analysis_phase|prompt_scaffolding|hidden|control|meta|internal)\s*:\s*[\s\S]*?\]/gi, '');
    // Curly template placeholders
    out = out.replace(/\{\{\s*[a-z_]+\s*\}\}/gi, '');
    // Leading "Label:" prefixes leaking at start of line
    out = out.replace(/^\s*(stage\s*name|analysis\s*phase|prompt\s*scaffolding|hidden\s*control(?:\s*text)?)\s*[:：].*$/gim, '');
    // Collapse excess blank lines
    out = out.replace(/\n{3,}/g, '\n\n').trim();
    const cleanContent = out || content.replace(/\[[\s\S]*?\]/g, '').trim();
    return { cleanContent, quickReplies: options, hasFrameworkCards };
  } catch {
    return { cleanContent: content, quickReplies: [], hasFrameworkCards: false };
  }
}

/**
 * Build a structured prompt from guided selections + optional free text.
 */
function buildStructuredInput(
  concern: string | null,
  duration: string | null,
  freeText: string,
  isZh: boolean,
): string {
  const parts: string[] = [];

  if (concern) {
    parts.push(isZh ? `我的主要困扰是：${concern}` : `My main concern: ${concern}`);
  }
  if (duration) {
    parts.push(isZh ? `持续时间：${duration}` : `Duration: ${duration}`);
  }
  if (freeText.trim()) {
    parts.push(isZh ? `补充描述：${freeText.trim()}` : `Additional details: ${freeText.trim()}`);
  }

  if (!concern && !duration) return freeText.trim();

  return parts.join('\n');
}

// ── Lightweight assistant for practitioner / learn intents ──
// Uses the existing `practitioner-assistant` edge function with topic mapping.
// Anonymous practitioners are capped at PRACTITIONER_GUEST_TURN_LIMIT turns
// to keep deep-AI resources reserved for client (support-seeking) conversations.
interface LightweightAssistantProps {
  intent: 'practitioner' | 'learn';
  onSwitchIntent: () => void;
  isAuthed: boolean;
  initialQuery?: string | null;
}
const LightweightAssistant: React.FC<LightweightAssistantProps> = ({ intent, onSwitchIntent, isAuthed, initialQuery }) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const T: AiTextMap = aiText(i18n.language);
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const composingRef = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const userTurns = msgs.filter((m) => m.role === 'user').length;
  const gated = !isAuthed && intent === 'practitioner' && userTurns >= PRACTITIONER_GUEST_TURN_LIMIT;

  useEffect(() => {
    // Scroll ONLY the inner conversation container, never the page.
    const viewport = scrollViewportRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [msgs, loading]);

  const title = intent === 'practitioner'
    ? (T.asst_practitioner_title)
    : (T.asst_learn_title);
  const subtitle = intent === 'practitioner'
    ? (T.asst_practitioner_subtitle)
    : (T.asst_learn_subtitle);

  const starters = intent === 'practitioner' ? T.starters_practitioner : T.starters_learn;

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || loading || gated) return;
    const next = [...msgs, { role: 'user' as const, content: text }];
    setMsgs(next);
    setSuggestions([]);
    setInput('');
    setLoading(true);
    try {
      const topic = intent === 'practitioner' ? 'platform' : 'learn';
      const { data, error } = await supabase.functions.invoke('practitioner-assistant', {
        body: { topic, messages: next },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const reply = (data as any)?.reply ?? '';
      const nextSuggestions = Array.isArray((data as any)?.suggestions)
        ? ((data as any).suggestions as string[]).slice(0, 5)
        : [];
      setMsgs([...next, { role: 'assistant', content: reply }]);
      setSuggestions(nextSuggestions);
    } catch (e: any) {
      toast.error(e?.message || (T.asst_failed));
      setMsgs(next);
    } finally {
      setLoading(false);
    }
  };

  // Auto-send an initial query when the user picked a sub-chip on the homepage.
  const initialSentRef = useRef(false);
  useEffect(() => {
    if (initialSentRef.current) return;
    if (!initialQuery || !initialQuery.trim()) return;
    initialSentRef.current = true;
    send(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  return (
    <div className="max-w-2xl mx-auto mb-10">
      <div className="text-center mb-4">
        <h2 className="text-2xl md:text-3xl font-serif text-foreground">
          {title}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
        <button
          type="button"
          onClick={onSwitchIntent}
          className="mt-1 text-xs text-brand-600 hover:text-brand-700 hover:underline"
        >
          {T.asst_switch_role}
        </button>
      </div>

      <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
        {msgs.length === 0 ? (
          <div className="p-4 space-y-2">
            <p className="text-xs text-muted-foreground">
              {T.asst_try_these}
            </p>
            {starters.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="block w-full text-left px-3 py-2 text-sm rounded-md border border-warm-200 bg-warm-50/40 text-foreground hover:border-brand-300 hover:bg-brand-50 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        ) : (
          <ScrollArea className="h-[280px]" ref={scrollViewportRef as any}>
            <div className="p-4 space-y-3">
              {msgs.map((m, i) => {
                const parsed = m.role === 'assistant' ? parseQuickReplies(m.content) : null;
                return (
                  <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                    <div
                      className={cn(
                        'max-w-[85%] text-sm rounded-2xl px-3 py-2',
                        m.role === 'user'
                          ? 'bg-brand-500 text-white rounded-tr-sm'
                          : 'bg-warm-50 text-foreground rounded-tl-sm'
                      )}
                    >
                      {m.role === 'assistant' ? (
                        <>
                          <AIMarkdown content={parsed!.cleanContent} />
                          {parsed!.hasFrameworkCards && (
                            <FrameworkCards onChipClick={(p) => send(p)} />
                          )}
                        </>
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-warm-50 text-muted-foreground text-sm rounded-2xl rounded-tl-sm px-3 py-2 inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {T.asst_thinking}
                  </div>
                </div>
              )}
              {!loading && suggestions.length > 0 && (
                <div className="pt-1">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                    {T.asst_keep_exploring}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="px-3 py-1.5 text-xs rounded-full border bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50 transition-all duration-200"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          </ScrollArea>
        )}

        {gated ? (
          <div className="border-t border-warm-100 p-4 bg-brand-50/40 text-center space-y-2">
            <p className="text-sm text-foreground">
              {T.asst_gate_msg}
            </p>
            <div className="flex justify-center gap-2">
              <Button
                size="sm"
                onClick={() => { saveRedirectPath('/dashboard'); navigate('/practitioner-auth'); }}
                className="bg-brand-500 hover:bg-brand-600 text-white rounded-full"
              >
                {T.asst_sign_in_up}
              </Button>
              <Button size="sm" variant="outline" onClick={onSwitchIntent} className="rounded-full">
                {T.asst_switch_role_btn}
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t border-warm-100 p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onCompositionStart={() => { composingRef.current = true; }}
              onCompositionEnd={() => { composingRef.current = false; }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (e.nativeEvent.isComposing || composingRef.current) return;
                  send(input);
                }
              }}
              placeholder={intent === 'practitioner'
                ? (T.asst_input_platform)
                : (T.asst_input_learn)}
              disabled={loading}
              className="flex-1 rounded-full border-warm-200"
            />
            <Button
              onClick={() => send(input)}
              disabled={loading || !input.trim()}
              size="icon"
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-full shrink-0"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground text-center pb-3 px-4 pt-2">
          {T.asst_disclaimer}
        </p>
      </div>
    </div>
  );
};


const WellnessConcierge: React.FC<WellnessConciergeProps> = ({ onStageRecommended }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { fastNavigate, prefetch, pendingPath } = useFastNavigation();
  const { user } = useAuth();
  
  const isZh = i18n.language?.startsWith('zh');
  const T: AiTextMap = aiText(i18n.language);
  const [inputValue, setInputValue] = useState('');
  const {
    messages,
    isLoading,
    recommendations,
    sendMessage: sendNavigatorMessage,
    uiState,
    setUiState,
    phase,
    
    pendingGuestResume,
    resumeGuestConversation,
    dismissGuestResume,
  } = useNavigatorChat();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCrisisState, setIsCrisisState] = useState(false);
  const [showInlineRecommendations, setShowInlineRecommendations] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // ── Role-aware intent ──
  // The first thing a homepage visitor picks. Determines whether we run the
  // full client-facing Wellness Concierge (deep AI), the lightweight
  // practitioner platform assistant, or a brief "learn about Resonow" guide.
  // Always start at the role selection on a fresh page load. Role choice is
  // explicit each visit; we don't auto-restore from localStorage because the
  // three paths are fundamentally different experiences and silently dropping
  // a user back into Practitioner Assistant is disorienting.
  const [intent, setIntent] = useState<HomepageIntent | null>(null);
  // Clear any legacy persisted intent from earlier builds.
  useEffect(() => {
    try { localStorage.removeItem(INTENT_STORAGE_KEY); } catch {}
  }, []);
  const [pendingIntentBranch, setPendingIntentBranch] = useState<'practitioner' | 'learn' | null>(null);
  const [pendingInitialQuery, setPendingInitialQuery] = useState<string | null>(null);
  const chooseIntent = useCallback((next: HomepageIntent, initialQuery?: string) => {
    setIntent(next);
    setPendingIntentBranch(null);
    setPendingInitialQuery(initialQuery ?? null);
  }, []);
  const resetIntent = useCallback(() => {
    setIntent(null);
    setPendingIntentBranch(null);
    setPendingInitialQuery(null);
  }, []);
  // Quick-reply chips reflect ONLY the current assistant turn — never persisted state.
  // This prevents stale chips from previous sessions/languages leaking into a new one.
  const lastAssistantContent = [...messages].reverse().find((m) => m.role === 'assistant')?.content || '';
  const parsedFromLast = parseQuickReplies(lastAssistantContent);
  // Single source of truth for AI panel language: i18n.language.
  // Filter out chips whose language doesn't match the current session language
  // so we never render mixed-language UI in the same turn.
  const chipLooksZh = (s: string) => /[\u4e00-\u9fff]/.test(s);
  const allChipsMatchLang = parsedFromLast.quickReplies.every((c) =>
    isZh ? chipLooksZh(c) : !chipLooksZh(c)
  );
  const quickReplyOptions = allChipsMatchLang ? parsedFromLast.quickReplies : [];
  if (typeof window !== 'undefined') {
    if (lastAssistantContent.includes('[quick_reply')) {
      console.debug('[WellnessConcierge] quick_reply parsed', {
        sessionLang: i18n.language,
        raw: lastAssistantContent.slice(-200),
        parsedCount: parsedFromLast.quickReplies.length,
        renderedCount: quickReplyOptions.length,
        droppedForLangMismatch: parsedFromLast.quickReplies.length - quickReplyOptions.length,
        options: parsedFromLast.quickReplies,
      });
    }
  }
  // Clear any persisted quick replies on mount and whenever UI language changes,
  // so chips always regenerate from the current session/language context.
  useEffect(() => {
    if (uiState.quickReplies && uiState.quickReplies.length > 0) {
      setUiState({ ...uiState, quickReplies: [] });
    }
    console.debug('[WellnessConcierge] session language changed', { sessionLang: i18n.language });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  const detectedStage = uiState.detectedStage ?? null;
  const userTurnCount = messages.filter((message) => message.role === 'user').length;
  const latestUserMessage = [...messages].reverse().find((message) => message.role === 'user')?.content || '';
  const hasServiceIntent = SERVICE_INTENT_PATTERN.test(latestUserMessage);
  const shouldShowBranchCard = !isLoading && !isCrisisState && userTurnCount >= 3 && userTurnCount <= 5 && !uiState.branchChoice && !hasServiceIntent;
  const shouldShowServicePanel = !isLoading && recommendations.length > 0 && (showInlineRecommendations || hasServiceIntent || uiState.branchChoice === 'services');
  const branchShownRef = useRef(false);
  const recoShownRef = useRef(false);
  const sessionStartedRef = useRef(false);

  const guestSessionId = !user?.id ? getGuestNavigatorSessionId() : null;

  useEffect(() => {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    trackAIEvent({
      event_type: 'session_start',
      guest_session_id: guestSessionId,
      context: 'homepage',
    } as any);
  }, [guestSessionId, user?.id]);

  useEffect(() => {
    if (shouldShowBranchCard && !branchShownRef.current) {
      branchShownRef.current = true;
      trackAIEvent({
        event_type: 'branch_card_shown',
        turn_index: userTurnCount,
        guest_session_id: guestSessionId,
        problem_type: classifyProblemType(latestUserMessage),
        context: 'homepage',
      });
    }
  }, [shouldShowBranchCard, userTurnCount, guestSessionId, latestUserMessage]);

  useEffect(() => {
    if (shouldShowServicePanel && !recoShownRef.current) {
      recoShownRef.current = true;
      trackAIEvent({
        event_type: 'recommendation_shown',
        turn_index: userTurnCount,
        guest_session_id: guestSessionId,
        recommended_service: true,
        stage: recommendations[0]?.chata_stage ?? null,
        problem_type: classifyProblemType(latestUserMessage),
        context: 'homepage',
      });
    }
  }, [shouldShowServicePanel, userTurnCount, guestSessionId, recommendations, latestUserMessage]);

  // Classify each completed AI assistant reply (structured, no full text stored)
  const lastClassifiedAssistantRef = useRef<string>('');
  useEffect(() => {
    if (isLoading) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) return;
    const fingerprint = `${messages.length}:${lastAssistant.content.length}`;
    if (lastClassifiedAssistantRef.current === fingerprint) return;
    lastClassifiedAssistantRef.current = fingerprint;
    const cls = classifyAIResponse(lastAssistant.content);
    trackAIEvent({
      event_type: 'ai_response',
      turn_index: userTurnCount,
      response_length: cls.length,
      contains_question: cls.containsQuestion,
      contains_recommendation: cls.containsRecommendation,
      response_type: cls.type,
      guest_session_id: guestSessionId,
      context: 'homepage',
      stage: typeof phase === 'number' ? phase : null,
    });
  }, [isLoading, messages, userTurnCount, guestSessionId, phase]);

  // Drop-off detection: when the user closes the tab mid-conversation,
  // capture the last turn + stage as exit point via sendBeacon.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      if (userTurnCount === 0) return;
      // Only fire if user hasn't clicked a clear conversion path
      const branched = !!uiState.branchChoice;
      if (branched) return;
      trackAIEventBeacon(
        {
          event_type: 'drop_off',
          guest_session_id: guestSessionId,
          context: 'homepage',
          exit_turn: userTurnCount,
          exit_stage: typeof phase === 'number' ? phase : null,
          turn_index: userTurnCount,
        },
        SUPABASE_URL,
        SUPABASE_PUBLISHABLE_KEY,
      );
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [userTurnCount, guestSessionId, uiState.branchChoice, phase]);

  // Guided input state
  const [selectedConcern, setSelectedConcern] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [guidedStep, setGuidedStep] = useState<'concern' | 'duration' | 'details'>('concern');

  const redirectToCrisisResources = useCallback(() => {
    forceCrisisRedirect({ delayMs: 600 });
  }, []);

  // Server-side crisis signal (phase === 'crisis' from AI metadata)
  useEffect(() => {
    if (phase === 'crisis' && !isCrisisState) {
      setIsCrisisState(true);
      trackAIEvent({
        event_type: 'crisis_detected',
        context: 'homepage',
        guest_session_id: guestSessionId,
        turn_index: userTurnCount,
      });
      toast.error(
        T.crisis_redirect,
        { duration: 1500 },
      );
      forceCrisisRedirect({ delayMs: 900 });
    }
  }, [phase, isCrisisState, isZh, guestSessionId, userTurnCount]);

  const {
    isRecording,
    transcript,
    interimTranscript,
    isSupported: speechSupported,
    startRecording,
    stopRecording,
    resetTranscript,
  } = useWebSpeechRecording({ language: '', continuous: false, interimResults: true });

  useEffect(() => {
    if (transcript) {
      setInputValue(prev => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  const concernChips = isZh ? CONCERN_CHIPS_ZH : CONCERN_CHIPS_EN;
  const durationChips = isZh ? DURATION_CHIPS_ZH : DURATION_CHIPS_EN;

  // ── Dynamic suggestion chips ──
  // Pick a chip stage that mirrors the AI's current posture so the chips feel
  // like a natural continuation of the dialogue rather than a fixed menu.
  const followUpSuggestions = useMemo<string[]>(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const userInputStats = lastUser ? classifyInputLength(lastUser.content) : null;
    const aiCls = lastAssistant ? classifyAIResponse(lastAssistant.content) : null;

    // Conversion stage — recommendations are visible OR AI explicitly
    // surfaced a recommendation OR the user expressed service intent.
    const inConversion =
      recommendations.length > 0 ||
      hasServiceIntent ||
      aiCls?.containsRecommendation === true ||
      uiState.branchChoice === 'services';

    let chipStage: ChipStage;
    if (inConversion) {
      chipStage = 'conversion';
    } else if (userTurnCount === 0) {
      // No user turn yet — fall back to early prompts (matches empty-state intent).
      chipStage = 'early';
    } else if (
      userTurnCount <= 1 ||
      userInputStats?.bucket === 'short' ||
      aiCls?.type === 'clarification'
    ) {
      // AI is still asking clarifying questions or input is very brief.
      chipStage = 'early';
    } else if (userTurnCount <= 3 || aiCls?.type === 'other') {
      chipStage = 'mid';
    } else {
      chipStage = 'late';
    }

    const pool = DYNAMIC_CHIPS[chipStage];
    return isZh ? pool.zh : pool.en;
  }, [messages, recommendations.length, hasServiceIntent, uiState.branchChoice, userTurnCount, isZh]);

  useEffect(() => {
    prefetch('/client-auth');
    prefetch('/signup');
    prefetch('/client-dashboard?tab=ai-navigator');
    prefetch('/booking-choice');
    prefetch('/book-an-appointment');
  }, [prefetch]);

  // handleGoDeeper defined after sendMessage (below).



  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStageClick = useCallback((stageNum: number) => {
    setUiState({ ...uiState, detectedStage: stageNum, clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), `stage:${stageNum}`])] });
    trackAIEvent({
      event_type: 'stage_click',
      stage: stageNum,
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      context: 'homepage',
    });
    if (onStageRecommended) {
      onStageRecommended(stageNum);
    }
  }, [onStageRecommended, setUiState, uiState, userTurnCount, guestSessionId]);

  const handleMicToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const markdownComponents = useMemo(() => ({
    strong: ({ children, ...props }: any) => {
      const text = String(children);
      const stageMatch = text.match(/Recommended\s*Stage:\s*Stage\s*(\d)/i) 
        || text.match(/推荐阶段[：:]\s*Stage\s*(\d)/i)
        || text.match(/推荐阶段[：:]\s*第(\d)阶段/i);
      
      if (stageMatch) {
        const stageNum = parseInt(stageMatch[1], 10);
        if (stageNum >= 0 && stageNum <= 4) {
          const stageName = isZh ? STAGE_NAMES[stageNum]?.zh : STAGE_NAMES[stageNum]?.en;
          return (
            <button
              onClick={() => handleStageClick(stageNum)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors cursor-pointer border-0"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isZh ? `进入 Stage ${stageNum} — ${stageName}` : `Go to Stage ${stageNum} — ${stageName}`}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          );
        }
      }
      return <strong {...props}>{children}</strong>;
    },
    a: ({ href, children, ...props }: any) => {
      if (href && href.startsWith('/book-an-appointment/')) {
        return (
          <button
            onClick={() => {
              setUiState({ ...uiState, clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), href])] });
              navigate(href);
            }}
            className="inline-flex items-center gap-1 text-brand-600 font-medium hover:text-brand-700 underline underline-offset-2 cursor-pointer border-0 bg-transparent p-0"
          >
            {children}
            <ArrowRight className="h-3 w-3" />
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [isZh, handleStageClick, navigate, setUiState, uiState]);

  const clientGated = !user && intent === 'client' && userTurnCount >= CLIENT_GUEST_TURN_LIMIT;

  const sendMessage = useCallback(async (input: string) => {
    if (!input.trim() || isLoading) return;
    // Soft cap for anonymous client visitors — preserve context, surface CTA.
    if (!user && intent === 'client' && userTurnCount >= CLIENT_GUEST_TURN_LIMIT) {
      trackAIEvent({
        event_type: 'guest_turn_limit_reached',
        context: 'homepage',
        guest_session_id: guestSessionId,
        turn_index: userTurnCount,
      } as any);
      toast.message(
        T.gate_continue_toast,
      );
      return;
    }
    if (isCrisisState) {
      redirectToCrisisResources();
      return;
    }
    // P0: Crisis keyword detection — force redirect, bypass all AI / recommendation flow
    if (detectCrisis(input)) {
      setIsCrisisState(true);
      trackAIEvent({
        event_type: 'crisis_detected',
        context: 'homepage',
        guest_session_id: guestSessionId,
        turn_index: userTurnCount + 1,
        input_text: input,
      });
      toast.error(
        T.crisis_redirect,
        { duration: 1500 },
      );
      forceCrisisRedirect({ delayMs: 900 });
      return;
    }
    setIsExpanded(true);
    const stats = classifyInputLength(input);
    trackAIEvent({
      event_type: 'user_message',
      turn_index: userTurnCount + 1,
      input_text: input,
      input_word_count: stats.words,
      input_char_count: stats.chars,
      input_length_bucket: stats.bucket,
      used_chip: !!selectedConcern,
      used_structured_input: !!(selectedConcern || selectedDuration),
      added_details: !!inputValue.trim() && (!!selectedConcern || !!selectedDuration),
      problem_type: classifyProblemType(input),
      guest_session_id: guestSessionId,
      context: 'homepage',
    });
    setUiState({ ...uiState, quickReplies: [], detectedStage: null });
    // Dynamic language detection: always follow the latest user input language so
    // assistant text, quick replies, and action buttons stay in sync with the user.
    const detectMsgLang = (text: string, fallback: string): string => {
      const cjk = text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/g)?.length || 0;
      const latin = text.match(/[A-Za-z]/g)?.length || 0;
      const total = cjk + latin;
      if (total < 2) return fallback;
      if (cjk / total >= 0.3) return 'zh';
      if (latin / total >= 0.5) return 'en';
      return fallback;
    };
    const previousLang = i18n.language;
    const msgLang = detectMsgLang(input, previousLang || 'en');
    if (msgLang !== previousLang) {
      try { await i18n.changeLanguage(msgLang); } catch (e) { console.warn('i18n.changeLanguage failed', e); }
    }
    console.log('[WellnessConcierge] dynamic language', {
      detectedFromUserInput: msgLang,
      sessionLangBefore: previousLang,
      sessionLangAfter: i18n.language,
      input: input.slice(0, 80),
    });
    await sendNavigatorMessage(input, msgLang);
  }, [isLoading, isCrisisState, redirectToCrisisResources, setUiState, uiState, sendNavigatorMessage, i18n, userTurnCount, selectedConcern, selectedDuration, inputValue, guestSessionId, user, intent, isZh]);

  // Handle sending with structured input enrichment
  const handleSend = useCallback(() => {
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    if (!structured.trim()) return;
    // Freeform typing implies the support path — auto-commit client intent.
    if (intent === null) chooseIntent('client');
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [inputValue, selectedConcern, selectedDuration, isZh, sendMessage, intent, chooseIntent]);

  const isComposingRef = useRef(false);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
    console.debug('[WellnessConcierge] compositionstart');
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
    console.debug('[WellnessConcierge] compositionend');
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    console.debug('[WellnessConcierge] keydown', {
      key: e.key,
      isComposing: e.nativeEvent.isComposing,
      composingRef: isComposingRef.current,
    });
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (e.nativeEvent.isComposing || isComposingRef.current) {
        console.debug('[WellnessConcierge] Enter suppressed — still composing');
        return;
      }
      console.debug('[WellnessConcierge] Enter sent message');
      handleSend();
    }
  };

  const handleConcernSelect = useCallback((value: string) => {
    if (selectedConcern === value) {
      setSelectedConcern(null);
      setGuidedStep('concern');
    } else {
      setSelectedConcern(value);
      setGuidedStep('duration');
    }
  }, [selectedConcern]);

  const handleDurationSelect = useCallback((value: string) => {
    if (selectedDuration === value) {
      setSelectedDuration(null);
    } else {
      setSelectedDuration(value);
      setGuidedStep('details');
    }
  }, [selectedDuration]);

  const handleQuickSend = useCallback(() => {
    if (!selectedConcern) return;
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [selectedConcern, selectedDuration, inputValue, isZh, sendMessage]);

  // Handle quick reply button click
  const handleQuickReply = useCallback((reply: string) => {
    setUiState({ ...uiState, quickReplies: [] });
    sendMessage(reply);
  }, [sendMessage, setUiState, uiState]);

  // ── Go Deeper Analysis (bridge to AI Navigator) ──
  // Exception rule: normal continuation chips stay inside Homepage AI, but
  // "Go Deeper Analysis" is an intentional transition into AI Navigator —
  // deeper reasoning, stage mapping, persistent progress tracking.
  // Anonymous users go through auth first; the guest conversation is
  // preserved in localStorage and migrated by useNavigatorChat on login.
  const handleGoDeeper = useCallback(() => {
    if (messages.length === 0) {
      toast.message(T.conv_share_first);
      return;
    }
    trackAIEvent({
      event_type: 'recommendation_click',
      event_label: 'go_deeper_to_navigator',
      turn_index: userTurnCount,
      stage: typeof phase === 'number' ? phase : null,
      guest_session_id: guestSessionId,
      target_path: user ? '/client-dashboard?tab=ai-navigator' : '/client-auth',
      context: 'homepage',
    } as any);
    toast.message(
      T.deep_enter,
      {
        description: T.deep_enter_desc,
        duration: 1800,
      },
    );
    if (user) {
      fastNavigate('/client-dashboard?tab=ai-navigator');
      return;
    }
    trackAIEvent({
      event_type: 'login_redirect',
      target_path: '/client-auth',
      guest_session_id: guestSessionId,
      context: 'homepage',
    } as any);
    saveRedirectPath('/client-dashboard?tab=ai-navigator');
    fastNavigate('/client-auth');
  }, [messages.length, isZh, userTurnCount, phase, guestSessionId, user, fastNavigate]);


  const continueInDashboard = useCallback(() => {
    const nextState = { ...uiState, branchChoice: 'continue' as const, branchPromptShownAt: new Date().toISOString() };
    setUiState(nextState);
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'continue',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      target_path: user ? '/client-dashboard?tab=ai-navigator' : '/client-auth',
      context: 'homepage',
    });
    if (!user) {
      trackAIEvent({
        event_type: 'login_redirect',
        target_path: '/client-auth',
        guest_session_id: guestSessionId,
        context: 'homepage',
      });
    }
    if (user) {
      fastNavigate('/client-dashboard?tab=ai-navigator');
      return;
    }
    saveRedirectPath('/client-dashboard?tab=ai-navigator');
    fastNavigate('/client-auth');
  }, [fastNavigate, setUiState, uiState, user, userTurnCount, guestSessionId]);

  const viewRecommendedServices = useCallback(() => {
    setUiState({
      ...uiState,
      branchChoice: 'services',
      branchPromptShownAt: new Date().toISOString(),
      clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), 'homepage:recommended-services'])],
    });
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'services',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      stage: recommendations[0]?.chata_stage ?? null,
      context: 'homepage',
    });
    setShowInlineRecommendations(true);
  }, [setUiState, uiState, userTurnCount, guestSessionId, recommendations]);

  const saveAndContinueLater = useCallback(() => {
    setUiState({ ...uiState, branchChoice: 'save_later', branchPromptShownAt: new Date().toISOString() });
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'save_later',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      target_path: user ? '/client-dashboard?tab=ai-navigator' : '/signup',
      context: 'homepage',
    });
    if (!user) {
      trackAIEvent({
        event_type: 'signup_redirect',
        target_path: '/signup',
        guest_session_id: guestSessionId,
        context: 'homepage',
      });
    }
    saveRedirectPath('/client-dashboard?tab=ai-navigator');
    fastNavigate(user ? '/client-dashboard?tab=ai-navigator' : '/signup');
  }, [fastNavigate, setUiState, uiState, user, userTurnCount, guestSessionId]);

  if (isCrisisState) {
    return (
      <div className="max-w-2xl mx-auto mb-10">
        <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 text-center space-y-4">
            <h2 className="text-xl md:text-2xl font-serif text-foreground">
              {T.crisis_get_help_title}
            </h2>
            <Button asChild size="lg" className="bg-brand-500 hover:bg-brand-600 text-white rounded-full">
              <a href={CRISIS_HELP_URL} target="_blank" rel="noopener noreferrer">
                {T.crisis_get_help_btn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasGuidedSelection = selectedConcern || selectedDuration;
  const canSendGuided = !!selectedConcern;
  const showGuidedInput = messages.length === 0 && !isCrisisState;

  // Intent gate — show role selection BEFORE entering any assistant.
  // Three paths are fundamentally different experiences, so the choice is
  // explicit (not inferred). After a selection, route into the matching
  // assistant below.
  const intentOptions: Array<{ key: HomepageIntent; emoji: string; label: string; hint: string }> = [
    { key: 'client',       emoji: '🫶', label: T.intent_client_label,       hint: T.intent_client_hint },
    { key: 'practitioner', emoji: '🌿', label: T.intent_practitioner_label, hint: T.intent_practitioner_hint },
    { key: 'learn',        emoji: '✨', label: T.intent_learn_label,        hint: T.intent_learn_hint },
  ];

  if (intent === null) {
    return (
      <div className="max-w-2xl mx-auto mb-10">
        <div className="text-center mb-4">
          <h2 className="text-2xl md:text-3xl font-serif text-foreground">
            {T.brand_homepage_intent_q}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {T.brand_homepage_intent_hint}
          </p>
        </div>
        <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {intentOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => chooseIntent(opt.key)}
                className="group flex flex-col items-start gap-2 rounded-xl border border-warm-200 bg-white px-4 py-4 text-left transition-all duration-200 hover:border-brand-400 hover:bg-brand-50/60 hover:shadow-sm"
              >
                <span className="text-2xl leading-none">{opt.emoji}</span>
                <span className="text-sm font-medium text-foreground">{opt.label}</span>
                <span className="text-[11px] text-muted-foreground">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Role-aware routing: practitioner or learn → lightweight assistant.
  if (intent === 'practitioner' || intent === 'learn') {
    return (
      <LightweightAssistant
        intent={intent}
        onSwitchIntent={resetIntent}
        isAuthed={!!user}
        initialQuery={pendingInitialQuery}
      />
    );
  }









  return (
    <div className="max-w-2xl mx-auto mb-10" data-resonow-dark-scope>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1" />
        <div className="flex-[3] text-center px-2">
          <h2 className="text-2xl md:text-3xl font-serif text-foreground">
            {T.brand_concierge_lead}
            <span className="text-brand-500 italic">{T.brand_concierge_name}</span>
          </h2>
          <button
            onClick={resetIntent}
            className="mt-1 text-xs text-muted-foreground hover:text-brand-600 underline-offset-2 hover:underline"
          >
            {T.conv_choose_path}
          </button>
          {user && messages.length > 0 && (
            <Button
              variant="link"
              className="mt-1 h-auto p-0 text-brand-600"
              onClick={() => navigate('/client-dashboard?tab=ai-navigator')}
            >
              {T.conv_continue_dashboard}
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="flex-1 flex justify-end" />

      </div>

      <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
        {pendingGuestResume && (
          <div className="px-4 pt-4">
            <SessionResumePrompt
              userTurns={pendingGuestResume.userTurns}
              lastUpdatedAt={pendingGuestResume.lastUpdatedAt}
              reachedBranch={pendingGuestResume.reachedBranch}
              onContinue={resumeGuestConversation}
              onStartFresh={dismissGuestResume}
            />
          </div>
        )}
        {/* Chat messages area */}
        {isExpanded && messages.length > 0 && (
          <ScrollArea ref={scrollAreaRef} className="h-[400px]">
            <div className="divide-y divide-warm-100">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex gap-3 px-4 py-3',
                    msg.role === 'user' ? 'flex-row-reverse' : '',
                    msg.role === 'assistant' ? 'bg-warm-50/50' : ''
                  )}
                >
                  <div className={cn(
                    'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5',
                    msg.role === 'user' ? 'bg-brand-100 text-brand-700' : 'bg-brand-50 text-brand-500'
                  )}>
                    {msg.role === 'user' ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                  </div>
                  <div className={cn(
                    'min-w-0 overflow-hidden max-w-[85%]',
                    msg.role === 'user' ? 'flex flex-col items-end' : ''
                  )}>
                    {msg.role === 'user' ? (
                      <div className="bg-brand-100 rounded-2xl rounded-tr-sm px-4 py-2.5">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ) : (
                      <div>
                        {(() => {
                          const parsed = parseQuickReplies(msg.content);
                          return (
                            <>
                              <AIMarkdown content={parsed.cleanContent} />
                              {parsed.hasFrameworkCards && (
                                <FrameworkCards onChipClick={(p) => sendMessage(p)} />
                              )}
                              {isLoading && idx === messages.length - 1 && (
                                <span className="inline-block w-1.5 h-4 bg-brand-400 animate-pulse ml-0.5 align-text-bottom" />
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick replies are rendered in the lower control area, not under the AI message. */}



              {/* Natural branch point — soft prose continuation, not an end-of-flow card */}
              {shouldShowBranchCard && (
                <div className="px-4 pt-3 pb-2">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {T.branch_intro}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <button
                      type="button"
                      onClick={continueInDashboard}
                      className="text-brand-700 hover:text-brand-800 hover:underline underline-offset-4"
                    >
                      {T.branch_continue}
                    </button>
                    <span className="text-muted-foreground/40" aria-hidden>·</span>
                    <button
                      type="button"
                      onClick={viewRecommendedServices}
                      className="text-brand-700 hover:text-brand-800 hover:underline underline-offset-4"
                    >
                      {T.branch_see_practitioners}
                    </button>
                    <span className="text-muted-foreground/40" aria-hidden>·</span>
                    <button
                      type="button"
                      onClick={saveAndContinueLater}
                      className="text-brand-700 hover:text-brand-800 hover:underline underline-offset-4"
                    >
                      {T.branch_save_later}
                    </button>
                  </div>
                </div>
              )}

              {/* Optional recommendations — shown by choice or explicit service intent */}
              {shouldShowServicePanel && (
                <div className="px-4 py-3 bg-brand-50/20">
                  <div className="rounded-lg border border-brand-200 bg-background p-3 space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      {T.rec_panel_title}
                    </p>
                    {recommendations[0] && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-xs text-muted-foreground">{T.rec_primary_stage}</p>
                          <p className="text-sm font-medium text-foreground">Stage {recommendations[0].chata_stage}</p>
                        </div>
                        <div className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-xs text-muted-foreground">{T.rec_supportive_stage}</p>
                          <p className="text-sm font-medium text-foreground">Stage {Math.max(0, Math.min(4, recommendations[0].chata_stage + 1))}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {recommendations.slice(0, 3).map((rec, index) => (
                        <div key={`${rec.name_en}-${index}`} className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-sm font-medium text-foreground">{isZh ? rec.name_zh : rec.name_en}</p>
                          <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
                          {rec.matched_practitioners?.slice(0, 2).map((practitioner) => (
                            <div key={practitioner.slug} className="mt-2 flex items-center justify-between gap-2 rounded border border-border bg-background p-2">
                              <span className="min-w-0 truncate text-xs font-medium text-foreground">{practitioner.name}</span>
                              <div className="flex shrink-0 gap-1">
                                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onMouseEnter={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onFocus={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onClick={() => { trackAIEvent({ event_type: 'recommendation_click', clicked_recommendation: true, target_path: `/book-an-appointment/${practitioner.slug}`, event_label: 'view_profile', stage: rec.chata_stage, guest_session_id: guestSessionId, context: 'homepage' }); fastNavigate(`/book-an-appointment/${practitioner.slug}`); }} disabled={pendingPath === `/book-an-appointment/${practitioner.slug}`}>{pendingPath === `/book-an-appointment/${practitioner.slug}` ? (T.rec_loading) : (T.rec_view_profile)}</Button>
                                <Button size="sm" className="h-7 px-2 text-xs bg-brand-500 hover:bg-brand-600 text-white" onMouseEnter={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onFocus={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onClick={() => { trackAIEvent({ event_type: 'recommendation_click', clicked_recommendation: true, target_path: `/book-an-appointment/${practitioner.slug}`, event_label: 'book', stage: rec.chata_stage, guest_session_id: guestSessionId, context: 'homepage' }); fastNavigate(`/book-an-appointment/${practitioner.slug}`); }} disabled={pendingPath === `/book-an-appointment/${practitioner.slug}`}>{T.rec_book}</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <Button size="sm" className="w-full bg-brand-500 hover:bg-brand-600 text-white" onMouseEnter={() => prefetch('/booking-choice')} onFocus={() => prefetch('/booking-choice')} onClick={() => fastNavigate('/booking-choice')} disabled={pendingPath === '/booking-choice'}>
                      {T.rec_view_bookable}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Stage navigation button */}
               {!isLoading && !isCrisisState && detectedStage !== null && (
                <div className="px-4 py-3 bg-brand-50/30">
                  <button
                    onClick={() => handleStageClick(detectedStage)}
                    className="w-full text-center text-sm font-medium text-brand-700 bg-brand-100 hover:bg-brand-200 border border-brand-300 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isZh
                      ? `查看 Stage ${detectedStage} 的所有疗愈师`
                      : `View All Stage ${detectedStage} Practitioners`}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        )}

        {/* Guided input section — only before first message */}


        {showGuidedInput && (
          <div className="px-4 pt-4 pb-2 space-y-3">
            {/* Step 1: Concern chips — client support flow. */}
            {(true) && (
            <div>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                <Heart className="h-3 w-3" />
                {T.chip_concern_header}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {concernChips.map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => handleConcernSelect(chip.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                      selectedConcern === chip.value
                        ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                        : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
            )}


            {/* Step 2: Duration chips — shown after concern selection */}
            {pendingIntentBranch === null && selectedConcern && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {T.chip_duration_header}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {durationChips.map((chip) => (
                    <button
                      key={chip.value}
                      onClick={() => handleDurationSelect(chip.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                        selectedDuration === chip.value
                          ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                          : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick send button when guided selections are made */}
            {pendingIntentBranch === null && canSendGuided && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300 flex items-center gap-2">
                <p className="text-xs text-muted-foreground flex-1">
                  {T.chip_send_hint}
                </p>
                <Button
                  onClick={handleQuickSend}
                  size="sm"
                  disabled={isLoading}
                  className="bg-brand-500 hover:bg-brand-600 text-white rounded-full text-xs px-4"
                >
                  {T.chip_analyze}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}


        {/* Input area */}
        <div className="p-4">
          {/* Show selected tags above input when guided */}
          {showGuidedInput && hasGuidedSelection && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedConcern && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {concernChips.find(c => c.value === selectedConcern)?.label}
                  <button onClick={() => { setSelectedConcern(null); setSelectedDuration(null); setGuidedStep('concern'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
              {selectedDuration && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {durationChips.find(d => d.value === selectedDuration)?.label}
                  <button onClick={() => { setSelectedDuration(null); setGuidedStep('duration'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
            </div>
          )}

          {clientGated && (
            <div className="mb-3 rounded-xl border border-brand-200 bg-brand-50/70 p-3 text-sm text-warm-900">
              <p className="font-medium mb-1">
                {T.gate_sign_in_title}
              </p>
              <p className="text-xs text-warm-700 mb-3">
                {T.gate_sign_in_desc}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="bg-brand-500 hover:bg-brand-600 text-white rounded-full"
                  onClick={() => { saveRedirectPath('/'); navigate('/signup'); }}
                >
                  {T.gate_create_account}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => { saveRedirectPath('/'); navigate('/client-auth'); }}
                >
                  {T.gate_sign_in}
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={inputValue + (interimTranscript ? interimTranscript : '')}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              placeholder={
                clientGated
                  ? (T.gate_placeholder)
                  : showGuidedInput
                  ? (T.input_guided)
                  : (T.input_continue)
              }
              disabled={isLoading || clientGated}
              className="flex-1 rounded-full border-warm-200"
            />
            {speechSupported && (
              <Button
                onClick={handleMicToggle}
                size="icon"
                variant={isRecording ? 'destructive' : 'ghost'}
                className={cn(
                  'rounded-full shrink-0 h-9 w-9',
                  isRecording ? 'animate-pulse' : 'text-muted-foreground/50 hover:text-muted-foreground'
                )}
                disabled={isLoading || clientGated}
              >
                {isRecording ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button
              onClick={handleSend}
              disabled={isLoading || clientGated || (!inputValue.trim() && !canSendGuided)}
              size="icon"
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-full shrink-0"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>


          {/* Quick replies & follow-ups — live in the lower control area as reply shortcuts. */}
          {messages.length > 0 && !isLoading && !isCrisisState && (quickReplyOptions.length > 0 || followUpSuggestions.length > 0) && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-2">
                {quickReplyOptions.length > 0
                  ? (T.conv_quick_reply)
                  : (T.conv_continue)}
              </p>
              <div className="flex flex-wrap gap-2">
                {(quickReplyOptions.length > 0 ? quickReplyOptions : followUpSuggestions).map((s) => {
                  // "Go deeper" is the bridge to AI Navigator (deep analysis + persistence).
                  const isDeepChip = s === 'Go deeper with analysis' || s === '深入分析';
                  // Other conversion-stage chips that should navigate instead of chat back.
                  const navTarget = (() => {
                    if (s === 'View practitioners' || s === '查看疗愈师') return '/booking-choice';
                    if (s === 'Book a session' || s === '预约一次咨询') return '/booking-choice';
                    return null;
                  })();
                  const isFromAI = quickReplyOptions.length > 0 && !isDeepChip && !navTarget;
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        if (isDeepChip) {
                          handleGoDeeper();
                        } else if (navTarget) {
                          fastNavigate(navTarget);
                        } else if (isFromAI) {
                          handleQuickReply(s);
                        } else {
                          sendMessage(s);
                        }
                      }}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-full transition-colors',
                        isDeepChip
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-brand-500 text-white hover:bg-brand-600'
                      )}
                    >
                      {isDeepChip && <Sparkles className="inline h-3 w-3 mr-1" />}
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}


          {/* Collapse toggle */}
          {messages.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center gap-1 w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {isExpanded
                ? (T.conv_collapse)
                : (T.conv_expand)}
            </button>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground text-center pb-3 px-4">
          {T.conv_disclaimer}
        </p>
      </div>
    </div>
  );
};

export default WellnessConcierge;
